import React, { useEffect, useRef, useState, useContext } from "react"
import * as Blog from "../Blog"
import * as Testimonials from "../Testimonials"
import ProfileAboutTab from "./ProfileAboutTab"
import PublicCredentials from "./PublicCredentials"
import PublicOverviewBento from "./PublicOverviewBento"
import PublicPortraitVideoDeck from "./PublicPortraitVideoDeck"
import PublicProfileServices from "./PublicProfileServices"
import ShareModal from "./ShareModal"
import { AllTestimonials } from "../../models/app.interface"
import { CoachPublicProfileContext } from "../../pages/manage-business/profile/context/coachPublicProfileContext"
import { NavLink, Route, Routes } from "react-router-dom"
import { ProfileRequests } from "../../api/app.service"
import { SvgArticle, SvgBusiness, SvgGrid, SvgList } from "../icons"
import { cn } from "../../utils/cn"

import { useSearchParams } from "react-router-dom"
import { useToast } from "../../hooks/useToast"
interface Props {
  services?: any[]
}

const PublicTilesCarousel: React.FC<Props> = ({ services }) => {
  const [shareModalOpen, setShareModalOpen] = useState<boolean>(false)
  const { coachPublicProfileData } = useContext(CoachPublicProfileContext)
  const endPoint = coachPublicProfileData?.endpoint_slug
  const topRef = useRef<HTMLDivElement>(null)
  const [testimonials, setTestimonials] = useState<AllTestimonials[]>([])
  const [videos, setVideos] = useState<any[]>([])
  const [posts, setPosts] = useState<any[]>([])
  const coachColor = coachPublicProfileData?.profile_information?.profileColor
  const { toast } = useToast()

  const tabs = [
    {
      name: "Overview",
      icon: <SvgGrid />,
      show: true,
      path: `/find-a-coach/${endPoint}/`,
    },
    {
      name: "About",
      icon: <SvgList />,
      show:
        coachPublicProfileData?.profile_information?.about ||
        coachPublicProfileData?.tags?.length > 0,
      path: "about",
    },
    {
      name: "Services",
      icon: <SvgBusiness />,
      show: Array.isArray(services) && services?.length > 0,
      path: "services",
    },
    {
      name: "Featured Content",
      icon: <SvgArticle />,
      show:
        (Array.isArray(videos) && videos?.length > 0) ||
        (Array.isArray(posts) && posts?.length > 0),
      path: "featured",
    },
    {
      name: "Testimonials",
      activeTab: "testimonials",
      icon: <SvgArticle />,
      show:
        Array.isArray(testimonials) &&
        testimonials?.length > 0 &&
        testimonials.some((testimonial: any) => testimonial?.is_shown === 1),
      path: "testimonials",
    },
  ]

  const getCoachTestimonials = async () => {
    await ProfileRequests.getTestimonials({
      coach_public_profile_id: coachPublicProfileData?.coach_public_profile_id,
    })
      .then((data: any) => {
        setTestimonials(data.testimonials)
      })
      .catch((ex: any) => {
        console.error(ex)
        toast.error(ex.response.data.message)
      })
  }

  const getVideos = () => {
    ProfileRequests.getCoachProfileVideos({
      coach_public_profile_id: coachPublicProfileData?.coach_public_profile_id,
    })
      .then((data) => {
        setVideos(data.videos)
      })
      .catch((ex) => {
        console.error(ex)
        toast.error(ex.response.data.message)
      })
  }

  const getCoachBlogPosts = async () => {
    await ProfileRequests.getCoachBlogPosts({
      coach_profile_id: parseInt(coachPublicProfileData?.coach_profile_id),
    })
      .then((data) => {
        const approvedPosts = data?.blog_posts?.filter(
          (post: any) => post.status === "approved" && post.published
        )
        setPosts(approvedPosts)
      })
      .catch((ex: any) => {
        console.error(ex)
        toast.error(ex.response.data.message)
      })
  }

  useEffect(() => {
    getCoachTestimonials()
    getVideos()
    getCoachBlogPosts()
  }, [])

  return (
    <>
      <div ref={topRef} className="relative flex w-full flex-col gap-[12px]">
        <div className="flex items-stretch gap-8 overflow-x-auto pb-4">
          {tabs
            .filter((tab) => tab.show)
            .map((tab, index) => (
              <NavLink
                key={index}
                end
                to={tab.path || ""}
                className={({ isActive }) =>
                  cn(
                    "inline-flex items-center justify-center rounded-lg text-sm font-semibold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 gap-2 whitespace-nowrap text-black shadow-md hover:bg-white font-bold border-px box-border h-10 px-4 py-2",
                    {
                      "bg-white": isActive,
                      "bg-grayFlash": !isActive,
                    }
                  )
                }
              >
                {tab.icon} <span>{tab.name}</span>
              </NavLink>
            ))}
        </div>
      </div>
      {shareModalOpen && (
        <ShareModal
          endpoint_slug={endPoint}
          shareModalOpen={shareModalOpen}
          setShareModalOpen={setShareModalOpen}
        />
      )}

      <Routes>
        <Route index element={<PublicOverviewBento />} />
        <Route path="services" element={<PublicProfileServices />} />
        <Route
          path="featured"
          element={
            <div>
              <div className="mb-16">
                <h1 className="mb-8 text-[20px] font-bold">Videos</h1>
                <PublicPortraitVideoDeck />
              </div>
              <div className="-mt-8">
                <h1 className="mb-8 text-[20px] font-bold">Blog</h1>
                <Blog.ListBlogCards coachColor={coachColor} />
              </div>
            </div>
          }
        />
        <Route
          path="testimonials"
          element={
            <Testimonials.ProfileTestimonialTab
              isEditable={false}
              coachPublicProfileData={coachPublicProfileData}
            />
          }
        />
        <Route
          path="about"
          element={
            <ProfileAboutTab coachPublicProfileData={coachPublicProfileData} />
          }
        />
        <Route path="blog" element={<Blog.ListBlogCards />} />
        <Route path="credentials" element={<PublicCredentials />} />
      </Routes>
    </>
  )
}

export default PublicTilesCarousel
